import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
} from '@mui/material'
import {
  MarkEmailRead,
  Delete,
  Notifications as NotificationsIcon,
  Warning,
  Error,
  Info,
  CheckCircle,
  Build,
  Schedule,
  Inventory2,
  Security,
  Event,
  DoneAll,
  FilterList,
} from '@mui/icons-material'
import { PageHeader, Button, Card, EmptyState } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'
import { useNavigate } from 'react-router-dom'

const NOTIFICATION_ICONS = {
  info: <Info />,
  warning: <Warning />,
  error: <Error />,
  success: <CheckCircle />,
  work_order: <Build />,
  pm_reminder: <Schedule />,
  low_stock: <Inventory2 />,
  warranty_expiry: <Security />,
  calibration_due: <Event />,
}

const NOTIFICATION_COLORS = {
  info: '#3b82f6',
  warning: '#f59e0b',
  error: '#ef4444',
  success: '#10b981',
  work_order: '#1B5E7A',
  pm_reminder: '#8b5cf6',
  low_stock: '#f97316',
  warranty_expiry: '#dc2626',
  calibration_due: '#0891b2',
}

const PRIORITY_COLORS = {
  low: 'default',
  medium: 'primary',
  high: 'warning',
  critical: 'error',
}

const NotificationItem = ({ notification, onMarkRead, onDelete, onClick }) => {
  const icon = NOTIFICATION_ICONS[notification.notification?.notification_type] || <NotificationsIcon />
  const color = NOTIFICATION_COLORS[notification.notification?.notification_type] || '#637381'
  const isUnread = !notification.is_read

  return (
    <Box
      sx={{
        p: 2,
        cursor: 'pointer',
        backgroundColor: isUnread ? 'rgba(27, 94, 122, 0.04)' : 'transparent',
        borderLeft: isUnread ? '3px solid #1B5E7A' : '3px solid transparent',
        transition: 'all 0.2s',
        '&:hover': {
          backgroundColor: 'rgba(27, 94, 122, 0.08)',
        },
      }}
      onClick={() => onClick(notification)}
    >
      <Box display="flex" gap={2}>
        <Avatar
          sx={{
            backgroundColor: `${color}20`,
            color: color,
            width: 40,
            height: 40,
          }}
        >
          {icon}
        </Avatar>
        <Box flex={1}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Typography
              variant="subtitle2"
              fontWeight={isUnread ? 600 : 400}
              color={isUnread ? 'text.primary' : 'text.secondary'}
            >
              {notification.notification?.title}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {notification.notification?.priority && (
                <Chip
                  label={notification.notification.priority}
                  size="small"
                  color={PRIORITY_COLORS[notification.notification.priority]}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
              <Typography variant="caption" color="text.secondary">
                {formatTimeAgo(notification.created_at)}
              </Typography>
            </Box>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {notification.notification?.message}
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
            {isUnread && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  onMarkRead(notification.id)
                }}
                title="Mark as read"
              >
                <MarkEmailRead fontSize="small" />
              </IconButton>
            )}
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation()
                onDelete(notification.id)
              }}
              title="Delete"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

const NotificationsList = () => {
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(0)
  const [typeFilter, setTypeFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const pageSize = 20
  const navigate = useNavigate()

  const fetchNotifications = async (pageNum = 1) => {
    try {
      setLoading(true)
      const params = { page: pageNum, page_size: pageSize }

      // Add filter params based on active tab
      if (activeTab === 1) params.is_read = false
      if (activeTab === 2) params.is_read = true
      if (typeFilter) params.notification_type = typeFilter

      const response = await api.get('/common/user-notifications/', { params })
      const data = response.data

      setNotifications(data.results || data)
      setTotalCount(data.count || (data.results || data).length)
      setTotalPages(Math.ceil((data.count || (data.results || data).length) / pageSize))
    } catch (error) {
      console.error('Error fetching notifications:', error)
      toast.error('Failed to load notifications')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNotifications(page)
  }, [page, activeTab, typeFilter])

  const handlePageChange = (event, newPage) => {
    setPage(newPage)
  }

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue)
    setPage(1) // Reset to first page when changing tabs
  }

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value)
    setPage(1) // Reset to first page when changing filter
  }

  const handleMarkRead = async (id) => {
    try {
      await api.post(`/common/user-notifications/${id}/mark_read/`)
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      )
    } catch (error) {
      console.error('Error marking as read:', error)
      toast.error('Failed to mark as read')
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await api.post('/common/user-notifications/mark_all_read/')
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })))
      toast.success('All notifications marked as read')
    } catch (error) {
      console.error('Error marking all as read:', error)
      toast.error('Failed to mark all as read')
    }
  }

  const handleDelete = async (id) => {
    try {
      await api.delete(`/common/user-notifications/${id}/`)
      setNotifications((prev) => prev.filter((n) => n.id !== id))
      toast.success('Notification deleted')
    } catch (error) {
      console.error('Error deleting notification:', error)
      toast.error('Failed to delete notification')
    }
  }

  const handleClick = (notification) => {
    if (!notification.is_read) {
      handleMarkRead(notification.id)
    }
    if (notification.notification?.link) {
      navigate(notification.notification.link)
    }
  }

  // Notification types for filter dropdown
  const notificationTypes = ['info', 'warning', 'error', 'success', 'work_order', 'pm_reminder', 'low_stock', 'warranty_expiry', 'calibration_due']

  return (
    <Box>
      <PageHeader
        title="Notifications"
        subtitle={`${totalCount} notification${totalCount !== 1 ? 's' : ''}`}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Notifications' },
        ]}
        actions={
          <Button startIcon={<DoneAll />} variant="outlined" onClick={handleMarkAllRead}>
            Mark All Read
          </Button>
        }
      />

      <Card>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="All" />
            <Tab label="Unread" />
            <Tab label="Read" />
          </Tabs>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>
              <Box display="flex" alignItems="center" gap={0.5}>
                <FilterList fontSize="small" /> Type
              </Box>
            </InputLabel>
            <Select
              value={typeFilter}
              onChange={handleTypeFilterChange}
              label="Type"
            >
              <MenuItem value="">All Types</MenuItem>
              {notificationTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {NOTIFICATION_ICONS[type]}
                    <span style={{ textTransform: 'capitalize' }}>{type.replace('_', ' ')}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider />

        {loading ? (
          <Box p={4} textAlign="center">
            <CircularProgress size={40} />
            <Typography color="text.secondary" mt={2}>Loading notifications...</Typography>
          </Box>
        ) : notifications.length === 0 ? (
          <EmptyState
            icon={NotificationsIcon}
            title="No notifications"
            description={activeTab === 1 ? "You're all caught up!" : "No notifications to show"}
          />
        ) : (
          <Box>
            {notifications.map((notification, index) => (
              <Box key={notification.id}>
                <NotificationItem
                  notification={notification}
                  onMarkRead={handleMarkRead}
                  onDelete={handleDelete}
                  onClick={handleClick}
                />
                {index < notifications.length - 1 && <Divider />}
              </Box>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" py={3}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </Box>
        )}
      </Card>
    </Box>
  )
}

export default NotificationsList
